import { useEffect, useState } from 'react';
import type { RefundBuyerDetail, RefundClaimRow } from '@chicare/shared';

import { api } from '@/lib/api';
import {
  useApproveRefund,
  useDeclineRefund,
  useRefundClaim,
  useRefundClaims,
} from '@/lib/adminQueries';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { AdminError } from './AdminError';

/**
 * The refund queue.
 *
 * ── WHY URGENCY IS THE LOUDEST THING ON SCREEN ──────────────────────────────
 *
 * The same ₦18,400 claim costs ChiCare ₦0 at hour eight and ₦18,400 at hour
 * fifty-four, because inside the settlement window the refund comes out of the
 * split before the pharmacy is ever paid. So the window — not the age, not the
 * value — is what the eye should land on first, and the queue arrives from the
 * API already sorted by it.
 *
 * ── WHY APPROVE IS NOT A ONE-TAP BUTTON ─────────────────────────────────────
 *
 * Approving SENDS money. It is not a status change. So it opens a panel that
 * states the amount, where it comes from, and what it will cost — and the
 * button says the amount rather than the word "Confirm".
 */
export function RefundReviewScreen() {
  const [status, setStatus] = useState('open');
  const [selected, setSelected] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch } = useRefundClaims(status);

  if (isError) {
    return (
      <AdminError title="We couldn't load the refund queue" error={error} onRetry={() => refetch()} />
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1100px] px-4 py-6 xl:px-8">
      <header className="mb-5">
        <h1 className="m-0 font-display text-[26px] font-bold tracking-[-0.02em]">Refunds</h1>
        <p className="mt-1 max-w-[70ch] text-[13px] leading-relaxed text-muted">
          Ordered by whichever settlement window closes first, not by age. A claim decided while
          its window is open costs ChiCare nothing — the refund comes out of the split before the
          pharmacy is paid.
        </p>
      </header>

      {data?.meta.gate_on ? (
        <div
          data-testid="refund-gate-banner"
          className="mb-5 flex flex-wrap items-start gap-3 rounded-card border border-caution bg-caution-tint p-4"
        >
          <Icon name="shield" size={18} className="mt-0.5 shrink-0 text-caution" />
          <div className="min-w-0">
            <p className="m-0 text-[13.5px] font-bold text-ink">
              Every refund is being held for review
            </p>
            <p className="m-0 mt-1 text-[12.5px] leading-relaxed text-muted">
              {data.meta.gate_reason ??
                'No reason was recorded. Add one in Settings → Refunds so the next person on shift knows why.'}{' '}
              Counterfeit claims still pay out automatically unless that was switched off too.
            </p>
          </div>
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {(
          [
            ['open', 'Open'],
            ['approved', 'Approved'],
            ['declined', 'Declined'],
            ['all', 'All'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => {
              setStatus(key);
              setSelected(null);
            }}
            className={cn(
              'min-h-11 rounded-btn border px-3.5 text-[13px] font-medium',
              status === key
                ? 'border-blue bg-blue text-white'
                : 'border-line bg-white text-muted hover:text-ink',
            )}
          >
            {label}
          </button>
        ))}

        {data ? (
          <span className="ml-auto font-mono text-[11.5px] text-faint">
            {data.meta.open_count} claim{data.meta.open_count === 1 ? '' : 's'} · reviewed within{' '}
            {data.meta.review_sla_hours}h
          </span>
        ) : null}
      </div>

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-28 w-full" radius="card" />
          <Skeleton className="h-28 w-full" radius="card" />
          <Skeleton className="h-28 w-full" radius="card" />
        </div>
      ) : data && data.claims.length === 0 ? (
        <EmptyState
          title="Nothing waiting"
          body={
            status === 'open'
              ? 'No refund claims need a decision right now.'
              : `No ${status} claims to show.`
          }
          action={
            status === 'all' ? (
              <Button variant="ghost" icon="refresh" onClick={() => refetch()}>
                Check again
              </Button>
            ) : (
              <Button variant="ghost" onClick={() => setStatus('all')}>
                See every claim
              </Button>
            )
          }
        />
      ) : (
        <div className="space-y-3">
          {data?.claims.map((claim) => (
            <ClaimCard
              key={claim.reference}
              claim={claim}
              open={selected === claim.reference}
              onToggle={() =>
                setSelected(selected === claim.reference ? null : claim.reference)
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * One claim, collapsed to what a reviewer can triage at a glance and expanded
 * to everything they need to decide.
 */
function ClaimCard({
  claim,
  open,
  onToggle,
}: {
  claim: RefundClaimRow;
  open: boolean;
  onToggle: () => void;
}) {
  const decided = claim.status !== 'open' && claim.status !== 'in_review';

  return (
    <Card className={cn('p-0 overflow-hidden', open && 'ring-1 ring-blue')}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full flex-col gap-3 p-4 text-left sm:flex-row sm:items-center"
      >
        <UrgencyDot urgency={claim.window.urgency} />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-[12px] font-medium text-ink">{claim.reference}</span>
            <Badge tone={toneFor(claim.reason)}>{humanise(claim.reason)}</Badge>
            {claim.requires_review ? <Badge tone="caution">Needs a person</Badge> : null}
            {decided ? <Badge tone="neutral">{claim.status}</Badge> : null}
          </div>

          <p className="m-0 mt-1.5 text-[13px] text-muted">
            <b className="font-semibold text-ink">{claim.buyer.name ?? 'Unknown buyer'}</b>
            {claim.buyer.tier_label ? (
              <span className={cn('ml-1.5', claim.buyer.restricted && 'font-semibold text-danger')}>
                · {claim.buyer.tier_label}
              </span>
            ) : null}
          </p>
          <p className="m-0 mt-0.5 text-[12.5px] text-muted">
            {claim.sub_order.pharmacy ?? 'Unknown pharmacy'} ·{' '}
            <span className="font-mono text-[12px]">{claim.sub_order.reference}</span>
          </p>
        </div>

        <div className="flex items-center gap-4 sm:flex-col sm:items-end sm:gap-0.5">
          <span className="font-mono text-[15px] font-bold tabular-nums text-ink">
            {claim.claimed_display}
          </span>
          <WindowLabel window={claim.window} />
        </div>

        <Icon
          name="chevron"
          size={16}
          className={cn('hidden shrink-0 text-faint transition-transform sm:block', open && 'rotate-90')}
        />
      </button>

      {open ? <ClaimDetail reference={claim.reference} /> : null}
    </Card>
  );
}

function ClaimDetail({ reference }: { reference: string }) {
  const { data, isLoading, isError, error, refetch } = useRefundClaim(reference);

  if (isError) {
    return (
      <div className="border-t border-line p-4">
        <AdminError title="We couldn't load this claim" error={error} onRetry={() => refetch()} />
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="space-y-3 border-t border-line p-4">
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  const { claim, context } = data;

  return (
    <div className="border-t border-line bg-paper p-4">
      {claim.buyer_detail && 'name' in claim.buyer_detail ? (
        <BuyerPanel buyer={claim.buyer_detail as RefundBuyerDetail} />
      ) : null}

      {claim.description ? (
        <section className="mb-4">
          <h3 className="m-0 mb-1.5 font-mono text-[10.5px] uppercase tracking-[0.12em] text-faint">
            What the buyer said
          </h3>
          <p className="m-0 max-w-[68ch] text-[13.5px] leading-relaxed text-ink">
            {claim.description}
          </p>
        </section>
      ) : null}

      {/* The number that decides an adverse-reaction claim. One report is a
          body; three against one batch is a defect, and the reviewer would be
          guessing without it. */}
      {context.reaction_signal ? (
        <section
          data-testid="reaction-signal"
          className={cn(
            'mb-4 rounded-card border p-3.5',
            context.reaction_signal.clustered
              ? 'border-danger bg-danger-tint'
              : 'border-line bg-white',
          )}
        >
          <h3 className="m-0 mb-1.5 font-mono text-[10.5px] uppercase tracking-[0.12em] text-faint">
            Batch signal
          </h3>
          <p
            className={cn(
              'm-0 text-[13px] font-medium leading-relaxed',
              context.reaction_signal.clustered ? 'text-danger' : 'text-ink',
            )}
          >
            {context.reaction_summary}
          </p>
          <dl className="m-0 mt-2.5 flex flex-wrap gap-x-6 gap-y-1 text-[12px] text-muted">
            <div className="flex gap-1.5">
              <dt>Alert threshold</dt>
              <dd className="m-0 font-mono tabular-nums text-ink">
                {context.reaction_signal.threshold}
              </dd>
            </div>
            <div className="flex gap-1.5">
              <dt>This buyer, 90 days</dt>
              <dd className="m-0 font-mono tabular-nums text-ink">
                {context.reaction_signal.buyer_reports_90d}
              </dd>
            </div>
          </dl>
        </section>
      ) : null}

      {claim.photo_count > 0 ? (
        <EvidencePhotos reference={claim.reference} count={claim.photo_count} />
      ) : null}

      <dl className="mb-4 grid grid-cols-1 gap-x-6 gap-y-2.5 text-[12.5px] sm:grid-cols-2">
        <Fact label="Order value" value={claim.sub_order.goods_total_display} mono />
        <Fact label="Claimed" value={claim.claimed_display} mono />
        <Fact
          label="Already refunded"
          value={context.already_refunded_kobo > 0 ? 'Yes — check before approving' : 'Nothing yet'}
        />
        <Fact
          label="Delivery code given"
          value={
            context.otp_verified_at
              ? new Date(context.otp_verified_at).toLocaleString('en-NG')
              : 'Never handed over'
          }
        />
        <Fact
          label="If approved, paid from"
          value={
            context.funding_if_approved === 'split'
              ? 'The split — costs ChiCare ₦0'
              : 'ChiCare, then recovered from the pharmacy'
          }
        />
        <Fact label="Filed" value={claim.filed_at ? new Date(claim.filed_at).toLocaleString('en-NG') : '—'} />
      </dl>

      {claim.status === 'open' || claim.status === 'in_review' ? (
        <DecisionPanel claim={claim} />
      ) : (
        <div className="rounded-card border border-line bg-white p-3.5">
          <p className="m-0 text-[13px] text-ink">
            <b className="capitalize">{claim.status}</b>
            {claim.decision_note ? ` — ${claim.decision_note}` : ''}
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Approve or decline.
 *
 * The approve button carries the amount rather than saying "Confirm", because
 * the person pressing it is moving real money and the last thing they read
 * should be how much.
 */
function DecisionPanel({ claim }: { claim: RefundClaimRow }) {
  const approve = useApproveRefund();
  const decline = useDeclineRefund();

  const [mode, setMode] = useState<'idle' | 'approve' | 'decline'>('idle');
  const [note, setNote] = useState('');
  // Generated once per panel, so a double tap on a slow connection is one refund.
  const [idempotencyKey] = useState(() => crypto.randomUUID());

  const busy = approve.isPending || decline.isPending;
  const failure = approve.error ?? decline.error;

  if (mode === 'idle') {
    return (
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => setMode('approve')} icon="check">
          Approve refund
        </Button>
        <Button variant="ghost" onClick={() => setMode('decline')}>
          Decline
        </Button>
      </div>
    );
  }

  return (
    <div className="rounded-card border border-line bg-white p-3.5">
      <label className="block">
        <span className="mb-1.5 block text-[12.5px] font-medium text-ink">
          {mode === 'approve'
            ? 'Note for the buyer (optional)'
            : 'Why are you declining? The buyer reads this.'}
        </span>
        <textarea
          value={note}
          onChange={(event) => setNote(event.target.value)}
          rows={3}
          className="w-full rounded-btn border border-line bg-paper p-2.5 text-[13px] text-ink outline-none focus:border-blue"
          placeholder={
            mode === 'approve'
              ? 'Batch confirmed heat-damaged in transit.'
              : 'The medicine is authentic and correctly dispensed. This appears to be an individual reaction rather than a problem with the product.'
          }
        />
      </label>

      {failure ? (
        <p role="alert" className="mt-2 text-[12.5px] font-medium text-danger">
          {failure instanceof Error ? failure.message : 'That did not go through.'}
        </p>
      ) : null}

      <div className="mt-3 flex flex-wrap gap-2">
        {mode === 'approve' ? (
          <Button
            disabled={busy}
            onClick={() =>
              approve.mutate({ reference: claim.reference, note: note || undefined, idempotencyKey })
            }
          >
            {busy ? 'Sending…' : `Send ${claim.claimed_display} back`}
          </Button>
        ) : (
          <Button
            disabled={busy || note.trim().length < 10}
            onClick={() => decline.mutate({ reference: claim.reference, note })}
          >
            {busy ? 'Saving…' : 'Decline this claim'}
          </Button>
        )}

        <Button variant="ghost" onClick={() => setMode('idle')} disabled={busy}>
          Cancel
        </Button>
      </div>

      {mode === 'approve' ? (
        <p className="m-0 mt-2.5 text-[12px] leading-relaxed text-muted">
          The money goes back to the card or account the buyer paid with. Banks usually take a few
          working days to show it.
        </p>
      ) : null}
    </div>
  );
}

function Fact({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex flex-wrap gap-x-2">
      <dt className="text-muted">{label}</dt>
      <dd className={cn('m-0 font-medium text-ink', mono && 'font-mono tabular-nums')}>{value}</dd>
    </div>
  );
}

/** Colour is never the only signal — the label beside it says the same thing. */
function UrgencyDot({ urgency }: { urgency: string }) {
  const tone =
    urgency === 'red'
      ? 'bg-danger'
      : urgency === 'amber'
        ? 'bg-caution'
        : urgency === 'green'
          ? 'bg-verified'
          : 'bg-faint';

  return <span className={cn('hidden h-2.5 w-2.5 shrink-0 rounded-full sm:block', tone)} aria-hidden />;
}

function WindowLabel({ window }: { window: RefundClaimRow['window'] }) {
  if (!window.open) {
    return <span className="text-[11.5px] font-medium text-faint">Window closed · we pay</span>;
  }

  const tone =
    window.urgency === 'red'
      ? 'text-danger'
      : window.urgency === 'amber'
        ? 'text-caution'
        : 'text-verified';

  return (
    <span className={cn('text-[11.5px] font-medium', tone)}>
      {window.urgency === 'red' ? 'Closes soon · free now' : 'Window open · free'}
    </span>
  );
}

function humanise(reason: string): string {
  return reason.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

function toneFor(reason: string): 'danger' | 'caution' | 'neutral' {
  if (reason === 'not_genuine') return 'danger';
  if (reason === 'adverse_reaction') return 'caution';

  return 'neutral';
}

/**
 * Who is asking for the money back.
 *
 * Placed above the claim description on purpose. A reviewer who reads "my
 * tablets arrived crushed" before they know this is the buyer's fourth claim
 * in ninety days has already started forming an answer.
 *
 * Nothing here is a verdict. A high claim count on a buyer who is upheld every
 * time usually means one bad pharmacy, which is a different problem worth
 * spotting — so the approved and declined counts sit side by side rather than
 * being collapsed into a single "risk" number that would hide exactly that.
 */
function BuyerPanel({ buyer }: { buyer: RefundBuyerDetail }) {
  const { claims } = buyer;
  const restricted = buyer.tier === 'restricted';

  return (
    <section
      data-testid="buyer-panel"
      className={cn(
        'mb-4 rounded-card border p-3.5',
        restricted ? 'border-danger bg-danger-tint' : 'border-line bg-white',
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="m-0 mb-1 font-mono text-[10.5px] uppercase tracking-[0.12em] text-faint">
            Who is asking
          </h3>
          <p className="m-0 text-[15px] font-bold text-ink">{buyer.name ?? 'Unknown buyer'}</p>
          <p className="m-0 mt-0.5 text-[12.5px] text-muted">
            {buyer.tier_label}
            {buyer.member_since ? ` · with ChiCare since ${buyer.member_since}` : null}
          </p>
        </div>

        {/* Half of these are resolved by a phone call rather than a refund, so
            the number is a link and not a string to copy out by hand. */}
        <div className="flex flex-wrap gap-2">
          {buyer.phone ? (
            <a
              href={`tel:${buyer.phone}`}
              className="inline-flex min-h-11 items-center gap-1.5 rounded-btn border border-line bg-white px-3 text-[12.5px] font-medium text-blue no-underline"
            >
              <Icon name="phone" size={13} />
              {buyer.phone}
            </a>
          ) : null}
          {buyer.email ? (
            <a
              href={`mailto:${buyer.email}`}
              className="inline-flex min-h-11 items-center gap-1.5 rounded-btn border border-line bg-white px-3 text-[12.5px] font-medium text-blue no-underline"
            >
              <Icon name="message" size={13} />
              <span className="max-w-[22ch] truncate">{buyer.email}</span>
            </a>
          ) : null}
        </div>
      </div>

      {restricted ? (
        <p className="m-0 mt-3 text-[12.5px] font-medium leading-relaxed text-danger">
          This buyer is restricted
          {buyer.restricted_until ? ` until ${buyer.restricted_until}` : ''}.
          {buyer.restriction_reason ? ` ${buyer.restriction_reason}` : ''}
        </p>
      ) : null}

      <dl className="m-0 mt-3 grid grid-cols-2 gap-x-5 gap-y-2 text-[12.5px] sm:grid-cols-4">
        <Stat label="Completed orders" value={String(buyer.completed_orders)} />
        <Stat
          label="Claims filed"
          value={
            claims.first_ever_claim ? 'First one' : `${claims.filed_all_time} · ${claims.filed_90d} in 90d`
          }
          tone={!claims.first_ever_claim && claims.filed_90d >= 3 ? 'warn' : undefined}
        />
        <Stat label="Upheld / declined" value={`${claims.approved} / ${claims.declined}`} />
        <Stat
          label="Refunded to date"
          value={claims.total_refunded_display}
          tone={claims.total_refunded_kobo > 0 ? 'warn' : undefined}
        />
      </dl>

      {buyer.unjustified_rejections_90d > 0 ? (
        <p className="m-0 mt-2.5 text-[12.5px] font-medium text-caution">
          {buyer.unjustified_rejections_90d} unjustified rejection
          {buyer.unjustified_rejections_90d === 1 ? '' : 's'} in the last 90 days.
        </p>
      ) : null}

      {/* A rider who could not find a flat and a buyer who was never in are
          different stories that both arrive as "it never came". */}
      {buyer.delivering_to ? (
        <p className="m-0 mt-2.5 text-[12.5px] leading-relaxed text-muted">
          <span className="text-faint">Delivered to </span>
          {[
            buyer.delivering_to.recipient_name,
            buyer.delivering_to.line1,
            buyer.delivering_to.line2,
            buyer.delivering_to.lga,
            buyer.delivering_to.state,
          ]
            .filter(Boolean)
            .join(', ')}
          {buyer.delivering_to.phone && buyer.delivering_to.phone !== buyer.phone ? (
            <span className="text-faint"> · on {buyer.delivering_to.phone}</span>
          ) : null}
        </p>
      ) : null}
    </section>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'warn' }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-[0.06em] text-faint">{label}</dt>
      <dd
        className={cn(
          'm-0 mt-0.5 font-mono text-[13px] font-medium tabular-nums text-ink',
          tone === 'warn' && 'text-caution',
        )}
      >
        {value}
      </dd>
    </div>
  );
}

/**
 * The buyer's photographs, fetched with the token.
 *
 * They live on the private disk — pictures of somebody's medicine and, for a
 * reaction, possibly of their own body — so there is no URL to put in an img
 * tag. Each is fetched as a blob through the admin gate and shown from an
 * object URL that is released when the claim is closed.
 */
function EvidencePhotos({ reference, count }: { reference: string; count: number }) {
  const [urls, setUrls] = useState<Array<string | null> | null>(null);

  useEffect(() => {
    let cancelled = false;

    Promise.all(
      Array.from({ length: count }, (_, index) =>
        api
          .refundEvidenceBlob(reference, index)
          .then((blob) => URL.createObjectURL(blob))
          .catch(() => null),
      ),
    ).then((loaded) => {
      if (cancelled) {
        loaded.forEach((url) => url && URL.revokeObjectURL(url));

        return;
      }

      setUrls(loaded);
    });

    return () => {
      cancelled = true;
      setUrls((current) => {
        current?.forEach((url) => url && URL.revokeObjectURL(url));

        return null;
      });
    };
  }, [reference, count]);

  return (
    <section className="mb-4">
      <h3 className="m-0 mb-1.5 font-mono text-[10.5px] uppercase tracking-[0.12em] text-faint">
        Photos from the buyer
      </h3>
      <ul className="m-0 grid list-none grid-cols-3 gap-2 p-0 sm:grid-cols-4 lg:grid-cols-6">
        {Array.from({ length: count }, (_, index) => {
          const url = urls?.[index];

          return (
            <li
              key={index}
              className="aspect-square overflow-hidden rounded-btn border border-line bg-white"
            >
              {urls === null ? (
                <Skeleton className="h-full w-full" />
              ) : url ? (
                <a href={url} target="_blank" rel="noreferrer" className="block h-full w-full">
                  <img
                    data-testid="evidence-photo"
                    src={url}
                    alt={`Evidence photo ${index + 1}`}
                    className="h-full w-full object-cover"
                  />
                </a>
              ) : (
                <span className="grid h-full place-items-center p-2 text-center text-[11px] text-muted">
                  Could not open
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
